<template>
  <div id="mainViewApp" style="height: 100%; position: relative; overflow: hidden">
    <div id="splitter" class="viewers-container" :class="{ 'empty-model': isEmptyModel }">
      <div class="left-info-container">
        <!-- Side panel has moved to layout/view.vue -->
        <!-- Model Browser panel removed - now embedded in Models panel as split view -->
        <!-- See src/components/models/modelsTab.vue for the new implementation -->
      </div>
      <div id="local-absolute-loading" v-if="localShowLoading || isCorrespondingPcLoading || pending360Zoom">
        <envizion-loader :size="48" />
        <div class="loading-progress-track">
          <div class="loading-progress-fill"></div>
        </div>
        <div class="loading-label">Preparing your workspace</div>
      </div>
      <div
        class="all-4-viewers-container"
        id="all-4-viewers-container"
        :class="{ 'w-100': !isDashboardOpened }"
        :style="{ height: allViewersHeight }">
        <VirtualKeyboard></VirtualKeyboard>
        <div
          id="left-side-viewers"
          class="all-2-viewers-container"
          :class="{
            'compare-right': compareToRight,
            'viewers-2-compare-mode': localShowComparasionTool,
            'rotate-all-2-viewers-container': isViewersParallelY,
          }"
          v-show="isView1Visible || isView2Visible">
          <PageView
            id="topleft-viewer"
            ref="viewPage1"
            :name="'top-left'"
            :slot_index="1"
            v-show="isView1Visible"
            :show_close_btn="showCloseBtn"
            :view_type="view1Type"
            @close_viewer="closeViewer($event, 1)"
            @change-view="changeView($event, 1)"
            :class="[
              compareToRight ? 'compare-right' : '',
              localShowComparasionTool ? 'view-compare-mode' : '',
            ]"
            :style="{ width: viewCompareWidth }">
          </PageView>
          <div v-show="isView2Visible" id="separator2"></div>
          <PageView
            id="bottomleft-viewer"
            ref="viewPage2"
            :name="'bottom-left'"
            :slot_index="2"
            v-show="isView2Visible"
            :show_close_btn="showCloseBtn"
            :view_type="view2Type"
            @close_viewer="closeViewer($event, 2)"
            @change-view="changeView($event, 2)"
            :class="[
              compareToRight ? 'compare-right' : '',
              localShowComparasionTool ? 'view-compare-mode' : '',
            ]"
            :style="{ width: viewCompareWidth }">
          </PageView>
        </div>
        <div v-show="isView3Visible" id="separator"></div>
        <div
          id="right-side-viewers"
          class="all-2-viewers-container"
          :class="{
            'compare-right': compareToRight,
            'viewers-2-compare-mode': localShowComparasionTool,
            'rotate-all-2-viewers-container': isViewersParallelY,
          }"
          v-show="isView3Visible || isView4Visible">
          <PageView
            id="topright-viewer"
            ref="viewPage3"
            :name="'top-right'"
            :slot_index="3"
            v-show="isView3Visible"
            :show_close_btn="showCloseBtn"
            :view_type="view3Type"
            @close_viewer="closeViewer($event, 3)"
            @change-view="changeView($event, 3)"
            :class="[
              compareToRight ? 'compare-right' : '',
              localShowComparasionTool ? 'view-compare-mode' : '',
            ]"
            :style="{ width: viewCompareWidth }">
          </PageView>
          <div v-show="isView4Visible" id="separator3"></div>
          <PageView
            id="bottomright-viewer"
            ref="viewPage4"
            :name="'bottom-right'"
            :slot_index="4"
            v-show="isView4Visible"
            :show_close_btn="showCloseBtn"
            :view_type="view4Type"
            @close_viewer="closeViewer($event, 4)"
            @change-view="changeView($event, 4)"
            :class="[
              compareToRight ? 'compare-right' : '',
              localShowComparasionTool ? 'view-compare-mode' : '',
            ]"
            :style="{ width: viewCompareWidth }">
          </PageView>
        </div>
        <div
          v-show="localShowComparasionTool"
          @mousedown="onDragStart"
          @touchstart="onDragStart"
          id="compare-separator"
          ref="compareSeparator"
          :style="{
            right: compareToRight ? panoSeparatorLeft : 'auto',
            left: !compareToRight ? panoSeparatorLeft : 'auto',
          }">
          <div style="height: 100%; display: flex; flex-direction: column">
            <div style="height: 50%"></div>
            <div
              class="compare-separator-circle fas fa-grip-lines-vertical"
              :style="{
                right: compareToRight ? panoSeparatorLeft : 'auto',
                left: !compareToRight ? panoSeparatorLeft : 'auto',
              }"></div>
          </div>
        </div>

        <MessagePopup></MessagePopup>
        <SuccessfulPopup
          v-if="localSuccessMessage"
          :message="localSuccessMessage"></SuccessfulPopup>
        <PointCloudsDisplayPopup v-if="showPointCloudDisplayPopup"></PointCloudsDisplayPopup>
        <AddPropertyPopup v-if="localShowAddProperty"></AddPropertyPopup>
        <CropLevelsPopup
          v-if="showCropLevelPopup"
          @on-close="showCropLevelPopup = false"></CropLevelsPopup>
        <CopiedText></CopiedText>

        <TagDynamicGraphPopup v-if="showTagDynamicGraph"></TagDynamicGraphPopup>
        <SelectDashboardPopup
          v-show="showSelectDashboardPopup"
          @open_selected_dashboard="toggleSelectedDashboard"></SelectDashboardPopup>
        <UpdateDocumentPopup v-if="showUpdateDocumentPopup"></UpdateDocumentPopup>
        <DocumentWorkspaceHost></DocumentWorkspaceHost>
        <PointCloudsDownloadInfo></PointCloudsDownloadInfo>
        <!-- <EditShareFilePopup @on-save="editShareFileForPointCloudDownload"></EditShareFilePopup> -->

        <!-- Old Quick Settings Button - Replaced by QuickSettingsFloating in view.vue -->
        <!-- <QuickSettingsButton></QuickSettingsButton> -->
      </div>

      <markups-popup></markups-popup>
      <markups-minimized-popup></markups-minimized-popup>

      <WorkPackProgressDashboard v-if="showWorkPackProgressDashboard"></WorkPackProgressDashboard>
      <SisTestingDashboard v-if="showSisTestingDashboard"></SisTestingDashboard>
      <InspectionsDashboard v-if="showInspectionDashboard"></InspectionsDashboard>
      <div v-if="showRealityVerticalSpace" class="dashboard-space"></div>
      <div v-if="notificationText" class="notification-div">{{ notificationText }}</div>
      <ScreenOverlay shaded></ScreenOverlay>
      <v-snackbar :value="isPickingCmlPoint" :timeout="-1" color="info" top>
        <v-icon left small>mdi-crosshairs-gps</v-icon>
        Click on the model to set CML point location.
        <template #action="{ attrs }">
          <v-btn text v-bind="attrs" @click="cancelCmlPicking"> Cancel </v-btn>
        </template>
      </v-snackbar>
      <!-- Position assignment snackbar (follows CML pattern above) -->
      <v-snackbar :value="isSettingTagPosition" :timeout="-1" color="info" top>
        <v-icon left small>mdi-map-marker-plus</v-icon>
        Setting position for: {{ positionAssignmentTagName }}. {{ positionAssignmentHint }}
        <template #action="{ attrs }">
          <v-btn text v-bind="attrs" @click="cancelPositionAssignment"> Cancel </v-btn>
        </template>
      </v-snackbar>
      <EditTask ref="editTask" />
      <AddToPlan ref="addToPlan" @save="addTaskToPlan" />
    </div>
  </div>
</template>

<script>
  // @workflow platform.viewer, tasks.show-in-3d
  /* eslint-disable no-unused-vars */
  /* eslint-disable no-debugger */
  import $ from 'jquery';
  import { ForgeMessage } from '@/utils/forgeMessageUtils';
  import { handleAssetInfo } from '@/store/helpers';
  import HttpService from '@/utils/httpService';
  import { ForgeProject } from '@/scripts/js/forgeModels';
  // Side panel moved to layout; keep only components still used here

  import {
    fetchAssetModel,
    fetchAssetBookmarks,
    fetchAssetCategories,
    fetchAssetModels,
    fetchAssetPointCloudDownloads,
    fetchAssetPointCloudModels,
    fetchUserSettings,
    fetchState,
    fetchAssetInspections,
    fetchAssetScaffold,
    fetchAssetAnnotations,
    fetchAssetNormalEquipment,
    fetchAssetManualPipelines,
    fetchAssetManualStructural,
  } from '@/utils/mongoUtils';
  import levelsService from '@/features/Admin/services/levels.service';
  import projectsService from '@/features/Admin/services/projects.service';
  import {
    disposeDocViewer,
    dispose2DViewer,
    dispose3DViewer,
    getViewPage3D,
    getViewPage360,
    getViewPage2d,
    disposeForgeViewers,
    drawMeasureLine,
    getMarkupsComponent,
    getStateId,
    showCopiedTextPopup,
    launchPDFViewer,
  } from '@/scripts/js/forgeviewer';
  import { GET_DOCUMENT_BY_ID } from '@/utils/attachmentsAPI';
  import MarkupsPopup from '@/components/markups/markupsPopup.vue';
  import MarkupsPopupMinimized from '@/components/markups/markupsPopupMinimized.vue';
  import permissions from '@Share/config/permissions';
  import { $truncateString } from '@/utils';
  import { PANEL_TAB, TAB_NAMES } from '@/constants/tabs';
  import { isRouteDashboardActive } from '@/constants/dashboards';
  import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
  import { decodeFromQuery as decodeShareIntent } from '@/utils/shareIntents';
  // Cross-viewer marker sync (Phase 1a): the singleton watcher is started from created()
  // (not at import time) to avoid the store circular-init chain reading an undefined store.
  import { startCrossViewerSync } from '@/composables/useCrossViewerSync';
  import {
    executePopup360Action,
    navigatePopup360ToPanorama,
    popup360FallbackConfirmation,
    resolvePopup360State,
    withScopedSpatialTargets,
  } from '@/features/Tags/utils/popup360Navigation';
  import { navigateToViewerWithAction } from '@/utils/viewerNavigationUtils';
  // The viewer adapter's page registration only (panes.js has no imports), so this view's import
  // graph does not grow; callers use the adapter itself, `@/platform/viewer`.
  import { registerViewerPanes } from '@/platform/viewer/panes';
  import { linkedTagsShortfallText, tagIdsFromQuery } from '@/utils/tagDeepLink';
  import { isolateLinkedTags } from '@/actions/tagLinks';
  import { showTagIn2d, tagPointOf } from '@/actions/tagIn2d';

  // ─────────────────────────────────────────────────────────────────────
  // Module-level guard set: tracks share-intent / legacy-redirect URLs
  // that have already been processed in THIS browser tab. Survives
  // MainView remounts (the per-instance `_urlHandlersExecuted` flag does
  // NOT — it resets on every remount, which fails for the facility ↔
  // kanban route transition triggered by taskBoardIntent.play's
  // router.replace).
  //
  // Without this, re-visiting an intent URL via in-app navigation (e.g.,
  // user clicks Back-to-Viewer after arriving on Kanban via task-link)
  // would cause MainView to remount, reset the per-instance guard, and
  // re-fire the share-intent — which then calls router.replace(kanban)
  // again, trapping the user in a loop.
  //
  // Per-tab semantics: the set lives at module scope, so it lasts as
  // long as the JS module instance does — i.e., until full page reload.
  // That's the correct lifetime — opening the URL in a NEW tab = new
  // JS module = empty set, intent fires normally. Same pattern as
  // `taskIsolationByTaskId` in src/features/Task/utils/taskIsolation.js
  // (cross-component coordination state at module scope).
  // ─────────────────────────────────────────────────────────────────────
  const HANDLED_REDIRECT_URLS = new Set();

  export default {
    name: 'MainView',
    components: {
      PageView: () => import('@/components/common/pageView.vue'),
      VirtualKeyboard: () => import('@/components/virtual-keyboard/virtual-keyboard.vue'),
      MessagePopup: () => import('@/components/common/messagePopup.vue'),
      ScreenOverlay: () => import('@/components/common/screenOverlay.vue'),
      // ModelBrowserTab registration removed - now embedded in modelsTab.vue
      SuccessfulPopup: () => import('@/components/common/successfulPopup.vue'),
      PointCloudsDisplayPopup: () => import('@/components/settings/pointCloudsDisplayPopup.vue'),
      AddPropertyPopup: () => import('@/components/modelBrowser/addPropertyPopup.vue'),
      CropLevelsPopup: () => import('@/components/common/cropLevelsPopup.vue'),
      CopiedText: () => import('@/components/common/copiedText.vue'),
      TagDynamicGraphPopup: () => import('@/components/common/tag-dynamic-graph.vue'),
      SelectDashboardPopup: () => import('@/components/common/selectDashboardPopup.vue'),
      UpdateDocumentPopup: () => import('@/components/attachments/update-document-popup.vue'),
      DocumentWorkspaceHost: () => import('@/features/Documents/components/DocumentWorkspaceHost.vue'),
      PointCloudsDownloadInfo: () =>
        import('@/components/pointcloudDownload/pointCloudsDownloadInfo.vue'),
      // EditShareFilePopup: () => import('@/components/shareFile/popups/shareFileEditPopup.vue'),
      InspectionsDashboard: () =>
        import('@/components/dashboard/inspection-dashboard/inspections-dashboard.vue'),
      SisTestingDashboard: () => import('@/components/dashboard/sis-testing-dashboard.vue'),
      WorkPackProgressDashboard: () =>
        import('@/components/workPackProgressDashboard/work-pack-progress-dashboard.vue'),
      'markups-popup': MarkupsPopup,
      'markups-minimized-popup': MarkupsPopupMinimized,
      // MenuLogo and MenuTitleBar registrations removed - were only used for floating Model Browser
      AddToPlan: () => import('@/components/common/AddToPlan.vue'),
      EditTask: () => import('@/features/Task/components/EditTask.vue'),
    },
    data() {
      return {
        permissions,
        notificationText: '',
        mouseInMenu: false,
        //comparasion
        // showComparasionTool: false,
        panoSeparatorLeft: '50%',
        // viewCompareWidth: 'auto',
        compareToRight: false,
        beforeCompareState: [],
        showBackBtn: false,
        showConnectIcons: false,
        showViewsOptions: false,
      };
    },
    created() {
      this.$store.state.mainApp = this;
      // The viewer adapter reaches this page's panes and 360 only through this registration
      // (src/platform/viewer/panes.js), never through the component itself.
      this._unregisterViewerPanes = registerViewerPanes({
        openPane: (type) => this.openViewPageWithType(type),
        showPane: (type) => this.showViewPageWithType(type),
        changePane: (slot, type) => this.changePaneType(slot, type),
        openTagIn360: (tagId, options) => this.openTagIn360(tagId, options),
      });
      this._popup360UrlFlight = null;
      startCrossViewerSync();
    },
    beforeDestroy() {
      // Clears the registration only while it is still this page's (a newer page may have registered).
      if (this._unregisterViewerPanes) this._unregisterViewerPanes();
    },
    deactivated() {
      // Cancel any in-flight cross-facility 360 zoom overlay + its watchdog, so a
      // kept-alive remount within the watchdog window can't let the old instance's
      // timer clear the new instance's overlay.
      this._clearPending360Zoom();
      const page360 = getViewPage360();
      if (page360) {
        if (this.localShowComparasionTool) {
          page360?.toggleComparasionTool();
        }

        page360?.closeViewer();
      }
    },
    mounted() {
      let tab = this.$route.query.tab;

      if (!!tab || tab == 0) {
        if (this.facility) {
          this.showPanel(tab);
        } else {
          this.$router.push({ name: 'facility' });
        }
      }

      // Get required data from the URL
      // this.$store.commit('setUserId', $getURLParameterVal('userId', false));
      // this.$store.commit('setIsAdminUser', !!$getURLParameterVal('isAdmin', false));
      // this.$store.commit('setAssetId', $getURLParameterVal('assetId', false));
      // this.$store.commit('setAssetName', $getURLParameterVal('assetName', true));
      // this.$store.commit('setShowActivityManagerBtn', true)
      // ForgeMessage.sendMessageToParent('toggle-activity-manager', { showActivityManager: false, fullWidth: false });
      // ForgeMessage.sendMessageToParent('get-tags-activities', { assetId: this.$store.state.assetId });
      // if (window.location.href.includes('invited=true&doNotRedirect=true'))
      //     this.view1Type = '3d';
      // else if (window.location.href.includes('stateId=')) {
      //     //To be handled automatically
      // }
      // else
      //     this.view1Type = '2d';
      // if (this.$store.state.defaultViewType || this.$store.state.defaultViewType == '3d')
      //     this.view1Type = '3d';
      // this.$store.state.mainApp = this;
      // fetchUserSettings(this.$store.state.userId);
      // this.init();
    },
    updated() {
      this.resizeViewers();
    },
    watch: {
      totalNavWidth() {
        if (this.localShowComparasionTool) {
          this.$nextTick(() => {
            this.resizeViewers();
          });
        }
      },
      filterDockInset() {
        // The gutter changes the viewer container width. 3D/2D self-heal via their own
        // ResizeObservers (forgeviewer.js:142-143,:229-230) — GATE-PROVEN. 360/Pannellum
        // and PDF do NOT, so drive them manually. Watch the INSET (not dockMode): collapse/
        // close/fallback flips change the inset without the mode changing.
        const app = this.$store.state.mainApp;
        if (!app) return; // stopForge() nulls mainApp (forgeviewer.js:334)
        this.$nextTick(() => app.resizeViewers());
        // Second pass after the 250ms --ds-transition-rail settles — no transitionend
        // listener (fragile when the property doesn't animate).
        setTimeout(() => {
          if (this.$store.state.mainApp) this.$store.state.mainApp.resizeViewers();
        }, 320);
      },
      facility: {
        deep: true,
        immediate: true,
        handler(facility) {
          if (!facility) return;
          // Cross-facility 360 zoom: raise the continuous "Preparing your
          // workspace" overlay BEFORE the same-id/else branch split. A cold-boot
          // new-tab deep link (?zoom360) takes the else->init() path whose
          // getAssetAsync flips showLoading off before the canonical handler runs,
          // so raising only in the same-id branch left the 2D flash on the
          // PRIMARY path. Gated on the zoom360 query.
          //
          // Idempotency: the handler's replaceState does NOT notify Vue
          // Router 2, so $route.query.zoom360 stays truthy for the rest of the
          // session and this deep watcher can re-fire on any facility mutation.
          // The same-id branch's HANDLED_REDIRECT_URLS guard does not cover the
          // else->init() path, so dedupe here keyed by fullPath: raise at most
          // once per zoom URL, else a re-fire after the panorama loaded would
          // re-show the overlay over a working viewer.
          if (
            this.$route.query?.tagId &&
            this.$route.query?.zoom360 === 'true' &&
            this._pending360ZoomRaisedFor !== this.$route.fullPath
          ) {
            this._pending360ZoomRaisedFor = this.$route.fullPath;
            this._raisePending360Zoom();
          }
          let assetId = this.$store.state.assetId;
          if (assetId === facility.id) {
            if (!this.$store.state.mainApp) this.$store.state.mainApp = this;
            // if (getViewPage3D()) getViewPage3D().startView();
            // if (getViewPage2d()) getViewPage2d().startView();

            // Handle URL-based navigation even when assetId matches
            // This handles cross-facility navigation (3D isolation or document opening)
            const query = this.$route.query;
            const hasTagIsolation = query.tagId && query.isolate === 'true';
            const hasTagZoom = query.tagId && query.zoom360 === 'true';
            const hasTagShow2D = query.tagId && query.show2d === 'true';
            const hasDocumentOpen = query.documentId && query.openDocument === 'true';
            const hasOpenPanel = query.tagId && query.openPanel === 'true';

            // SHARE_INTENT registry: typed deep-link bus. Each share intent
            // (task-expand today, more in future) registers encode/decode/play
            // in src/utils/shareIntents/. Decoupled from this file — adding new
            // share types doesn't require changes here.
            const shareIntent = decodeShareIntent(query);

            if (
              hasTagIsolation ||
              hasTagZoom ||
              hasTagShow2D ||
              hasDocumentOpen ||
              hasOpenPanel ||
              shareIntent
            ) {
              // Cross-mount idempotency: skip dispatch if this redirect URL
              // was already handled in this tab. Without this, MainView's
              // remount on facility↔kanban transition would reset the
              // per-instance _urlHandlersExecuted flag and re-fire the
              // handler, trapping the user in a loop. See HANDLED_REDIRECT_URLS
              // declaration at the top of this file for full rationale.
              const redirectKey = this.$route.fullPath;
              if (HANDLED_REDIRECT_URLS.has(redirectKey)) {
                debugLog('[MainView] redirect URL already handled this tab — skipping', {
                  redirectKey,
                });
                return;
              }

              // Per-instance guard: prevents double-fire WITHIN a single mount
              // when the facility watcher fires twice during initial asset load.
              if (this._urlHandlersExecuted) return;
              this._urlHandlersExecuted = true;
              HANDLED_REDIRECT_URLS.add(redirectKey);

              // Need to load asset data before opening viewers
              this.getAssetAsync().then(() => {
                if (hasTagIsolation || hasTagZoom || hasOpenPanel) {
                  this.handleTagSpatialNavigationFromUrl();
                }
                if (hasTagShow2D) {
                  this.handleTagShow2DFromUrl();
                }
                if (hasDocumentOpen) {
                  this.handleDocumentOpenFromUrl();
                }
                if (shareIntent) {
                  this.$store.dispatch('SHARE_INTENT/play', shareIntent);
                }
              });
            }
          } else {
            this.$store.commit('setAssetId', facility.id);
            this.$store.commit('setAssetName', facility.name);
            fetchUserSettings();
            this.init();
          }
        },
      },
      $route(to, from) {
        let tab = to.query.tab;

        if (!!tab || tab == 0) {
          this.showPanel(tab);
        }
      },
      selectedTab: function (tab) {
        if (tab || tab == 0) {
          setTimeout(() => {
            switch (tab) {
              // case 12:
              //   this.toggleActivityManager();
              //   break;
              // case 13:
              //   this.toggleDashboard();
              //   break;
              // case 14:
              //   this.toggleLayouts();
              //   break;

              default:
                this.showPanel(tab);

                break;
            }
          }, 100);
          this.$store.commit('setIsTabShown', true);
        } else {
          this.closeLeftTab();
        }
      },
      isTabShown: function () {
        // Watcher preserved for potential future use
      },
      isDashboardFullScreen: function (value) {
        setTimeout(() => {
          this.resizeViewers();
        }, 10);
      },
      isDashboardOpened: function (value) {
        if (!value) {
          this.$router.push({ name: 'facility' });
        }
      },
      isviewrMinimized: function (value) {
        if (value == true) {
          this.$store.dispatch('closeLeftTab', value);
        } else {
          if (this.selectedTab) {
            this.showPanel(this.$store.state.lastOpenTabIndex);
          }
        }
      },
      areViewsLocked: function (value) {
        debugLog(`Views Locked: ${value}`);
      },
      isPickingCmlPoint(newVal, oldVal) {
        if (!newVal && oldVal) {
          this.resetCmlPickingCursor();
        }
      },
    },
    methods: {
      $truncateString,
      // toggleCollapseModelBrowserPanel removed - Model Browser now embedded in Models panel
      editShareFileForPointCloudDownload(id) {
        this.$store.state.editingPointCloudDownloadInfo.downloadUrl = id;
        HttpService.post({
          url: 'api/pointClouds/editPointCloudDownload',
          data: this.$store.state.editingPointCloudDownloadInfo,
        })
          .then(() => {
            this.$dialog.notify.success('Custom PC info updated successfully.', {
              position: 'top-right',
              timeout: 3000,
            });
          })
          .catch((error) => {
            debugError(error);
          });
      },
      addTaskToPlan(data) {
        let taskTypeId = data.plans;
        let taskType = this.$store.getters['TASK_MANAGER/GET_ENTRIES_BY_USER'].find(
          (o) => o.id == taskTypeId,
        );
        let statusId = null;
        if (taskType && taskType.statuses.length > 0) {
          statusId = taskType.statuses[0]?.id;
        }
        let today = new Date().toISOString();
        let task = {
          typeId: taskTypeId,
          text: '',
          start_date: today,
          end_date: today,
          actualStartDate: null,
          actualEndDate: null,
          forgeTagsIds: data.items.map((item) => item.id),
          statusId: statusId,
        };
        let payLoad = {
          task,
          isNew: true,
          fromIndexPage: false,
          taskTypeId,
          isBulk: false,
        };
        this.$refs.editTask.open(payLoad);
      },
      toggleDashboard() {
        if (
          this.showRealityDashboard ||
          this.showInspectionDashboard ||
          this.showSisTestingDashboard ||
          this.showWorkPackProgressDashboard
        ) {
          this.toggleSelectedDashboard();
        } else {
          this.$store.commit('SET_SHOW_SELECT_DASHBOARD_POPUP', true);
        }
      },
      toggleSelectedDashboard(selectedDashboard) {
        let showDashboardAM =
          !this.showInspectionDashboard &&
          !this.showSisTestingDashboard &&
          !this.showWorkPackProgressDashboard &&
          !this.showRealityDashboard &&
          selectedDashboard;

        this.$store.commit('setIsRealityDashoardShown', showDashboardAM);

        ForgeMessage.sendMessageToParent('toggle-dashboard', {
          showDashboard: this.showRealityDashboard,
          selectedDashboard: selectedDashboard,
        });

        this.$store.commit('hideThumbnailPopup', '2d');
        this.$store.commit('hideThumbnailPopup', '3d');
        this.$store.commit('hideThumbnailPopup', '360');
      },
      openViewPageWithType(type) {
        // Block new 360 slot when one is preserved (crop session active)
        if (type === '360' && this.$store.getters['GET_PANORAMA_PRESERVED_IN_SLOT']) {
          return 0;
        }
        if (
          (this.isView1Visible && this.view1Type == type) ||
          (this.isView2Visible && this.view2Type == type) ||
          (this.isView3Visible && this.view3Type == type) ||
          (this.isView4Visible && this.view4Type == type)
        ) {
          return 0;
        }
        if (!this.view1Type && this.view1Type != type) {
          this.isView1Visible = true;
          this.view1Type = type;
          return 1;
        } else if (!this.view3Type && this.view3Type != type) {
          this.isView3Visible = true;
          this.view3Type = type;
          return 3;
        } else if (!this.view4Type && this.view4Type != type) {
          this.isView4Visible = true;
          this.view4Type = type;
          return 4;
        } else if (!this.view2Type && this.view2Type != type) {
          this.isView2Visible = true;
          this.view2Type = type;
          return 2;
        } else {
          this.$dialog.notify.warning(
            `All viewers are in use, please close any of them and try again.`,
            {
              position: 'top-right',
              timeout: 3000,
            },
          );
        }
      },
      /**
       * Switch the view in `slot` to `type` through that PageView's own view switcher (the user's
       * "change view" menu: its changeView, then this page's changeView), so both paths stay one.
       * @returns {boolean} false when there is no such slot
       */
      changePaneType(slot, type) {
        const page = this.$refs[`viewPage${slot}`];
        if (!page || typeof page.changeView !== 'function') return false;
        page.changeView(type);
        return true;
      },
      showViewPageWithType(type) {
        if (!this.isView1Visible && this.view1Type == type) {
          this.isView1Visible = true;
        } else if (!this.isView2Visible && this.view2Type == type) {
          this.isView2Visible = true;
        } else if (!this.isView3Visible && this.view3Type == type) {
          this.isView3Visible = true;
        } else if (!this.isView4Visible && this.view4Type == type) {
          this.isView4Visible = true;
        }
      },
      hideViewPageWithType(type) {
        if (this.isView1Visible && this.view1Type == type) {
          this.isView1Visible = false;
        } else if (this.isView2Visible && this.view2Type == type) {
          this.isView2Visible = false;
        } else if (this.isView3Visible && this.view3Type == type) {
          this.isView3Visible = false;
        } else if (this.isView4Visible && this.view4Type == type) {
          this.isView4Visible = false;
        }
      },
      changeView(newType, index) {
        var existingType = this[`view${index}Type`];
        if (existingType == newType) return;

        if (existingType == 'doc' || newType == 'doc') disposeDocViewer();

        const preservedSlot = this.$store.getters['GET_PANORAMA_PRESERVED_IN_SLOT'];

        if (existingType == '360') {
          if (preservedSlot === index) {
            // Crop workflow set the flag — skip destroy, let watcher handle
          } else {
            // Normal switch or explicit close — destroy as before
            if (this.forgeService.panoramaViewer) {
              this.forgeService.panoramaViewer.destroy();
              this.forgeService.panoramaViewer = null;
              this.$store.state.selectedImage = null;
            }
          }
        } else if (newType == '360') {
          if (preservedSlot === index) {
            // Restoring preserved — keep flag so pageView watcher detects it
          } else if (this.forgeService.panoramaViewer) {
            // Different slot wants 360 — destroy existing
            this.forgeService.panoramaViewer.destroy();
            this.forgeService.panoramaViewer = null;
            this.$store.state.selectedImage = null;
            this.$store.commit('CLEAR_PANORAMA_PRESERVED');
          }
        }

        // Clear this type from other slots (existing behavior)
        if (this.view1Type == newType) this.view1Type = '';
        if (this.view2Type == newType) this.view2Type = '';
        if (this.view3Type == newType) this.view3Type = '';
        if (this.view4Type == newType) this.view4Type = '';
        this[`view${index}Type`] = newType;
      },
      startLiveReview() {
        if (!getViewPage3D()) {
          this.$dialog.notify.warning(`Please open 3d view first.`, {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }
        $('#toolbar-collaborateTool').click();
        //change the base URL to the parent URL
        var ext = this.viewer.getExtension('Autodesk.Viewing.Collaboration');

        if (!ext) return;

        if (ext.isActive()) {
          this.collabIntervalId = setInterval(function () {
            var input = $('#collabBox .collabBoxInputText');
            if (input.length > 0 && input[0].value.includes(window.location.href)) {
              clearInterval(this.collabIntervalId);
              var existingSite = input[0].value;
              var forgeParams = existingSite.replace(window.location.href, '');
              var newURL = `${this.$store.state.parentSiteUrl}?assetId=${this.$store.state.assetId}&${forgeParams}`;
              input[0].value = newURL;
              input[0].select();
            }
          }, 1000);
        } else {
          clearInterval(this.collabIntervalId);
        }
      },
      resizeViewers() {
        if (this.viewer) this.viewer.resize();
        if (this.forgeService && this.forgeService.panoramaViewer) {
          this.forgeService.panoramaViewer.resize();
          let canvas = document.getElementById('pano-canvas');
          if (canvas) {
            canvas.remove();
            let viewer360 = getViewPage360();
            if (viewer360) {
              getViewPage360().updatePanoDimensionLine();
            }
          }
        }
        if (this.$store.state.planViewer) this.$store.state.planViewer.resize();
        if (this.$store.state.pdfviewer && this.$store.state.pdfViewerLoaded)
          this.$store.state.pdfviewer.resize();
      },
      openGlobalSearch() {
        this.showPanel(PANEL_TAB.SEARCH);
      },
      /**
       * Opens a panel tab - delegates to store SHOW_PANEL action
       * @param {number} tabIndex - Tab index (use PANEL_TAB constants)
       * @param {boolean} pinned - Whether to pin the left menu
       */
      showPanel(tabIndex, pinned = false) {
        this.showViewsOptions = false;
        this.$store.dispatch('SHOW_PANEL', { tabIndex, pinned });
      },
      init() {
        debugLog('MainView init');
        this.$store.commit('setShowLoading', true);

        this.getAssetAsync().then(() => {
          setTimeout(() => {
            // if (getViewPage3D()) getViewPage3D().startView();
            // if (getViewPage2d()) getViewPage2d().startView();
            if (this.localShowComparasionTool && getViewPage360())
              getViewPage360().toggleComparasionTool();
          }, 0);

          // Guard: prevent double invocation when both watcher and init call getAssetAsync
          if (this._urlHandlersExecuted) return;
          this._urlHandlersExecuted = true;

          // Resolve one canonical tag spatial intent after the facility is loaded.
          this.handleTagSpatialNavigationFromUrl();

          // Handle tag 2D show from URL params (cross-facility 2D plan navigation)
          this.handleTagShow2DFromUrl();

          // Handle document auto-open from URL params (cross-facility P&ID navigation)
          this.handleDocumentOpenFromUrl();

        });
      },
      async handleTagSpatialNavigationFromUrl() {
        const {
          tagId, isolate, zoom360, openPanel, show2d, openDocument,
        } = this.$route.query;
        if (!tagId || show2d === 'true' || openDocument === 'true') return;

        let intent = null;
        if (zoom360 === 'true') intent = 'explicit-360';
        else if (isolate === 'true') intent = 'isolate-3d';
        else if (openPanel === 'true') intent = 'info';
        if (!intent) return;
        const isPopup360Request = zoom360 === 'true';
        // Several tags (`tagIds`, a task opened from another facility, utils/tagDeepLink.js).
        const linked = tagIdsFromQuery(this.$route.query);
        const isolatesSeveral = intent === 'isolate-3d' && linked.tagIds.length > 1;

        try {
          let navigationOutcome;
          if (intent === 'explicit-360') navigationOutcome = await this.handlePopup360FromUrl(tagId);
          else if (isolatesSeveral) navigationOutcome = await this.isolateTagsFromLink(linked.tagIds);
          else {
            navigationOutcome = await this.$store.dispatch('NAVIGATE_TAG_SPATIALLY', {
              tagId,
              assetId: this.$store.state.assetId,
              intent,
              isolate: intent === 'isolate-3d',
              selectedFacilityId: null,
              source: 'deep-link',
            });
          }
          if (navigationOutcome?.status === 'failed' || navigationOutcome?.status === 'unavailable') {
            this.$dialog.notify.error(isolatesSeveral
              ? 'Unable to show these tags in the 3D viewer.'
              : 'Unable to open this tag in the requested viewer.');
          } else if (intent === 'isolate-3d' && linked.total > linked.tagIds.length) {
            this.$dialog.notify.warning(linkedTagsShortfallText(linked.tagIds.length, linked.total));
          }
        } catch (err) {
          debugError('Failed to navigate to tag from URL:', err);
          this.$dialog.notify.error('Failed to open tag');
        } finally {
          const currentQuery = this.$route.query;
          const replacedByNewPopup360 = isPopup360Request
            && currentQuery.zoom360 === 'true'
            && currentQuery.tagId !== tagId;
          if (!replacedByNewPopup360) this._clearPending360Zoom();
          const requestStillOwnsQuery = !isPopup360Request
            || (currentQuery.zoom360 === 'true' && currentQuery.tagId === tagId);
          if (requestStillOwnsQuery) window.history.replaceState({}, '', this.$route.path);
        }
      },
      /** Several tags from a deep link: the shared isolate action (actions/tagLinks.js). */
      async isolateTagsFromLink(tagIds) {
        const result = await isolateLinkedTags({ store: this.$store, router: this.$router }, tagIds);
        if (!result.ok) debugWarn('[MainView] deep link: tags not isolated', { tagIds, result });
        return { status: result.ok ? 'executed' : 'failed' };
      },
      async handlePopup360FromUrl(tagId) {
        const facilityId = this.$store.state.assetId;
        const flightKey = `${String(facilityId).toLowerCase()}:${String(tagId).toLowerCase()}`;
        if (this._popup360UrlFlight?.key === flightKey) {
          return this._popup360UrlFlight.promise;
        }
        const flight = { key: flightKey, promise: null };
        flight.promise = this.executePopup360FromUrl(tagId, facilityId).finally(() => {
          if (this._popup360UrlFlight === flight) this._popup360UrlFlight = null;
        });
        this._popup360UrlFlight = flight;
        return flight.promise;
      },
      executePopup360FromUrl(tagId, facilityId) {
        return this.openTagIn360(tagId, {
          facilityId,
          isWanted: () => this.$route.query?.tagId === tagId
            && this.$route.query?.zoom360 === 'true',
        });
      },
      /**
       * Opens a tag in 360 in this facility the way the ?zoom360 deep link does: the scoped tag,
       * the facility's scans, the popup's own choice (linked scan, else nearest after a confirm).
       * Also the AVA Assistant's ui.tags.open_360 (services/viewerHost.js), which has no link.
       * The newest request wins; `isWanted` lets the caller withdraw (the link: its query changed).
       * @returns {Promise<{ status: string, reason?: string }>}
       */
      async openTagIn360(tagId, { facilityId = this.$store.state.assetId, isWanted = () => true } = {}) {
        const requestId = (Number.isFinite(this._popup360UrlRequestId)
          ? this._popup360UrlRequestId
          : 0) + 1;
        this._popup360UrlRequestId = requestId;
        const shouldExecute = () => requestId === this._popup360UrlRequestId
          && this.$store.state.assetId === facilityId
          && isWanted();
        const tag = await this.hydratePopup360UrlTag(tagId, facilityId, shouldExecute);
        if (!shouldExecute()) return { status: 'stale', reason: 'superseded-intent' };

        let projects = Array.isArray(this.$store.state.projects)
          ? this.$store.state.projects
          : [];
        if (projects.length === 0) {
          const loadedProjects = await this.$store.dispatch('LOAD_FACILITY_PROJECTS', facilityId);
          if (!shouldExecute()) return { status: 'stale', reason: 'superseded-intent' };
          const currentProjects = Array.isArray(this.$store.state.projects)
            ? this.$store.state.projects
            : [];
          projects = currentProjects.length > 0
            ? currentProjects
            : (Array.isArray(loadedProjects) ? loadedProjects : []);
        }
        const popupState = resolvePopup360State(tag, {
          currentFacilityId: facilityId,
          currentFacilityImages: projects.flatMap(project => project.images || []),
          facilityDict: this.$store.getters.getFacilityDict || {},
        });
        if (!popupState.localAction) {
          return { status: 'unavailable', reason: 'no-popup-360-action' };
        }

        return executePopup360Action({
          tag,
          action: popupState.localAction,
          currentFacilityId: facilityId,
          shouldExecute,
          dependencies: this.popup360UrlDependencies(),
        });
      },
      async hydratePopup360UrlTag(tagId, facilityId, shouldExecute) {
        const baseTag = await this.$store.dispatch('GET_BASE_TAG_BY_ID', {
          id: tagId,
          assetId: facilityId,
          expectedKind: null,
        });
        if (!shouldExecute()) return baseTag;
        const tagType = baseTag?.discriminator || baseTag?.type;
        if (!tagType) return baseTag;
        try {
          const typedTag = await this.$store.dispatch('FETCH_TAG_BY_TYPE_AND_ID', {
            type: tagType,
            id: tagId,
          });
          return withScopedSpatialTargets(typedTag, baseTag);
        } catch (err) {
          debugWarn('Popup 360 typed hydration failed; using scoped base tag.', err);
          return baseTag;
        }
      },
      popup360UrlDependencies() {
        const forgeService = this.$store.state.forgeService;
        return {
          navigateToPanorama: action => navigatePopup360ToPanorama(
            callback => navigateToViewerWithAction('360', callback),
            action,
          ),
          getNearestImage: tag => forgeService.getClosestImageToPoint(
            typeof tag?.getPointInView === 'function'
              ? tag.getPointInView()
              : (tag?.point || tag?.position),
          ),
          openAtScanPosition: (tag, options) => forgeService.openTagAtScanPosition(tag, options),
          openAtLinkedSource: (tag, options) => forgeService.openTagAtLinkedSource(tag, options),
          confirmNearest: () => this.$dialog.confirm(popup360FallbackConfirmation()),
        };
      },

      // Cross-facility 360 zoom (?zoom360) loader coordination. The
      // "Preparing your workspace" overlay stays visible from intent detection until
      // the canonical executor reaches a terminal state, so the default 2D slot
      // never flashes. The watchdog remains a backstop for an unforeseen stalled
      // viewer load; post-destroy it only clears a Vuex flag.
      _raisePending360Zoom() {
        // Ultimate backstop window only — every known terminal (panorama load,
        // already-open scan, no-scan/null, abort paths, teardown) clears the flag
        // explicitly; the watchdog fires solely if an unforeseen path (e.g. a
        // getAssetAsync rejection that skips the canonical handler) strands it.
        const WATCHDOG_MS = 25000;
        this.$store.commit('setPending360Zoom', true);
        debugLog('[ZoomFromUrl] pending360Zoom=true (continuous overlay armed)');
        if (this._pending360ZoomWatchdog) clearTimeout(this._pending360ZoomWatchdog);
        this._pending360ZoomWatchdog = setTimeout(() => {
          this.$store.commit('setPending360Zoom', false);
          this._pending360ZoomWatchdog = null;
          debugWarn('[ZoomFromUrl] pending360Zoom watchdog fired — force-cleared overlay');
        }, WATCHDOG_MS);
      },
      _clearPending360Zoom() {
        this.$store.commit('setPending360Zoom', false);
        if (this._pending360ZoomWatchdog) {
          clearTimeout(this._pending360ZoomWatchdog);
          this._pending360ZoomWatchdog = null;
        }
      },

      /**
       * Handle tag 2D show from URL query params.
       * Called when opening a facility from another facility's 2D button.
       * Loads the tag, switches slot 1 to 2D, then runs the shared "show a tag on the 2D plan"
       * action (actions/tagIn2d.js). Cross-facility tags may have a level2D this facility doesn't
       * have: `orSelectedLevel` falls back to the selected level (facility default) rather than
       * showing the "No Level found" error.
       */
      async handleTagShow2DFromUrl() {
        const { tagId, show2d } = this.$route.query;
        if (!tagId || show2d !== 'true') return;

        try {
          const tag = await this.$store.dispatch('GET_BASE_TAG_BY_ID', {
            id: tagId,
            assetId: this.$store.state.assetId,
            expectedKind: null,
          });

          if (!tag || !tagPointOf(tag)) {
            debugLog('[Show2DFromUrl] Tag not found or has no position in this facility:', tagId);
            return;
          }

          debugLog('[Show2DFromUrl] Showing tag in 2D plan:', tagId);

          // Switch slot 1 to 2D view
          this.changeView('2d', 1);
          await this.$nextTick();

          // The adapter is loaded on demand, so this view's import graph does not grow.
          const { viewerFor } = await import('@/platform/viewer');
          const shown = await showTagIn2d(
            { store: this.$store, viewer: viewerFor(this.$store) },
            tag,
            { orSelectedLevel: true },
          );
          debugLog('[Show2DFromUrl] 2D plan:', shown.status, shown.levelName || '');
        } catch (err) {
          debugError('Failed to load tag for 2D show from URL:', err);
        } finally {
          window.history.replaceState({}, '', this.$route.path);
        }
      },

      /**
       * Handle document auto-open from URL query params.
       * Called when opening a facility from another facility's P&ID button.
       * Changes view1 to 'doc' type instead of opening a new viewer slot.
       */
      async handleDocumentOpenFromUrl() {
        const { documentId, openDocument, tagId } = this.$route.query;

        if (!documentId || openDocument !== 'true') return;

        try {
          // 1. Fetch document using established utility (handles SAS token + URL decoding)
          const document = await GET_DOCUMENT_BY_ID(documentId);

          if (!document) {
            debugWarn('Document not found:', documentId);
            this.$dialog?.notify?.warning?.('Document not found', {
              position: 'top-right',
              timeout: 3000,
            });
            return;
          }

          // 2. Set tag to highlight if provided
          if (tagId) {
            this.$store.commit('setTagIdToHighlight', tagId);
          }

          // 3. Change view1 from '2d' to 'doc' (instead of opening new viewer)
          this.changeView('doc', 1);

          // 4. Create ForgeAttachment and launch PDF viewer
          this.$store
            .dispatch('GET_DOCUMENT_BY_REFERENCE_ID', {
              documentId: document.documentId || document.id,
              storageReference: document.storageReference,
              name: document.fileName || document.name,
            })
            .then((forgeAttachment) => {
              // Store the attachment and launch viewer
              this.$store.commit('setActiveAttachment', forgeAttachment);
              setTimeout(() => {
                launchPDFViewer(forgeAttachment);
              }, 100);
            });

          // 5. Clean up URL params
          window.history.replaceState({}, '', this.$route.path);
        } catch (error) {
          debugError('Failed to load document from URL:', error);
          this.$dialog?.notify?.error?.('Failed to load document', {
            position: 'top-right',
            timeout: 3000,
          });
        }
      },

      restoreState(stateId) {
        fetchState(stateId, (state) => {
          if (!state) {
            this.$store.commit('setShowLoading', false);
            return;
          }
          this.$store.state.fetchedState = state;
          let assetId = this.$store.state.assetId;
          this.fetchAndApplyProject(assetId, () => {
            //set selected level first so when viewer2d launch, it opens it directly
            if (state.viewer2DSettings) {
              let level = this.$store.state.levels.find(
                (o) => o.planUrn == state.viewer2DSettings.urn,
              );
              this.$store.state.selectedLevel = level;
            }
            this.isView1Visible = state.isView1Visible;
            this.isView2Visible = state.isView2Visible;
            this.isView3Visible = state.isView3Visible;
            this.isView4Visible = state.isView4Visible;
            this.isLeftMenuPinned = state.isLeftMenuPinned;
            this.$store.commit('setIsTabShown', state.isTabShown);
            if (state.isTabShown) this.$store.commit('setSelectedTab', state.selectedTab);
            this.view1Type = state.view1Type;
            this.view2Type = state.view2Type;
            this.view3Type = state.view3Type;
            this.view4Type = state.view4Type;
            document.getElementById('left-side-viewers').style.width = state.leftSideViewersWidth;
            document.getElementById('right-side-viewers').style.width = state.rightSideViewersWidth;
            document.getElementById('topleft-viewer').style.height = state.topleftViewerHeight;
            document.getElementById('bottomleft-viewer').style.height =
              state.bottomleftViewerHeight;
            document.getElementById('topright-viewer').style.height = state.topRightViewerHeight;
            document.getElementById('bottomright-viewer').style.height =
              state.bottomRightViewerHeight;
            setTimeout(() => {
              this.resizeViewers();
            }, 100);
            this.$store.commit('setShowComparasionTool', state.showComparasionTool);
            this.panoSeparatorLeft = state.panoSeparatorLeft;
            this.viewCompareWidth = state.viewCompareWidth;
            if (this.localShowComparasionTool && getViewPage360()) {
              setTimeout(() => {
                getViewPage360().setPanoWidth(this.panoSeparatorLeft);
                getViewPage360().containerOpacity = state.container360Opacity;
                // getViewPage360().showComparasionTool = state.showComparasionTool;
              }, 100);
            }
            //panoViewerSettings
            if (state.panoViewerSettings) {
              setTimeout(() => {
                let project = this.$store.state.projects.find((o) =>
                  o.images.some((e) => e.name == state.panoViewerSettings.image.name),
                );
                if (project) {
                  let image = project.images.find(
                    (o) => o.name == state.panoViewerSettings.image.name,
                  );
                  image.isLocked = state.isLockedViewer3DAndPano;
                  this.forgeService.launchPanoViewer(image);
                  this.forgeService.panoramaViewer.setYaw(state.panoViewerSettings.yaw);
                  this.forgeService.panoramaViewer.setPitch(state.panoViewerSettings.pitch);
                  this.forgeService.panoramaViewer.setHfov(state.panoViewerSettings.fov);
                  this.forgeService.panoramaViewer.resize();
                  if (
                    state.viewer3DSettings.measuringLineStart &&
                    state.viewer3DSettings.measuringLineEnd &&
                    getViewPage360()
                  ) {
                    getViewPage360().drawPanoMeasureStartPoint(
                      state.viewer3DSettings.measuringLineStart.pitch,
                      state.viewer3DSettings.measuringLineStart.yaw,
                    );
                    getViewPage360().drawPanoMeasureEndPoint(
                      state.viewer3DSettings.measuringLineEnd.pitch,
                      state.viewer3DSettings.measuringLineEnd.yaw,
                    );
                    getViewPage360().updatePanoDimensionLine();
                  }
                }
              }, 100);
            }
            //viewer2DSettings
            if (state.viewer2DSettings) {
              setTimeout(() => {
                if (this.$store.state.planViewer) {
                  // this.$store.state.planViewer.restoreState(
                  //   state.viewer2DSettings.viewerState,
                  //   false,
                  //   true,
                  // );
                  let planExtension = this.forgeService.getDrawPointsExtension();
                  if (
                    planExtension &&
                    state.viewer2DSettings.measuredArea &&
                    state.viewer2DSettings.measuredArea.length > 0
                  ) {
                    planExtension.areaPoints = state.viewer2DSettings.measuredArea;
                    planExtension.measuringDone = true;
                    planExtension.updatePolygonGl();
                  }
                  if (state.viewer2DSettings.measuringLine) {
                    let measure2DEx = this.forgeService.getMeasureExtensionInViewer2D();
                    drawMeasureLine(state.viewer2DSettings.measuringLine, measure2DEx);
                  }
                }
              }, 1500);
            }
            //markupsViewerSettings
            if (state.markupsViewerSettings && state.markupsViewerSettings.urn) {
              let annotation = this.$state.annotations.find(
                (o) => o.id == state.markupsViewerSettings.annotationGuid,
              );
              if (annotation) {
                let markup = annotation.markups.find(
                  (o) => o.urn == state.markupsViewerSettings.urn,
                );
                if (markup) {
                  getMarkupsComponent().show(
                    annotation,
                    markup,
                    state.markupsViewerSettings.pageId,
                  );
                  setTimeout(() => {
                    getMarkupsComponent().markupViewer.restoreState(
                      state.markupsViewerSettings.viewerState,
                      false,
                      true,
                    );
                  }, 2000);
                }
              }
            }
          });
        });
      },
      dragElement_CompareTool(element, direction) {
        if (!element) return;
        let _this = this;
        let md; // remember mouse down info
        element.addEventListener('mousedown', onDragStart);
        element.addEventListener('touchstart', onDragStart);
        function onDragStart(e) {
          const isTouch = e.type === 'touchstart';
          md = {
            e,
            offsetLeft: element.offsetLeft,
            firstWidth: getViewPage360().$el.parentElement.offsetWidth,
          };
          const moveEvent = isTouch ? 'touchmove' : 'mousemove';
          const endEvent = isTouch ? 'touchend' : 'mouseup';
          document.addEventListener(moveEvent, onDragMove);
          document.addEventListener(endEvent, onDragEnd);
        }
        function onDragMove(e) {
          const isTouch = e.type === 'touchmove';
          const clientX = isTouch ? e.changedTouches[0].clientX : e.clientX;
          const clientY = isTouch ? e.changedTouches[0].clientY : e.clientY;
          const mdClientX = isTouch ? md.e.changedTouches[0].clientX : md.e.clientX;
          const mdClientY = isTouch ? md.e.changedTouches[0].clientY : md.e.clientY;
          const delta = {
            x: clientX - mdClientX,
            y: clientY - mdClientY,
          };
          if (direction === 'H') {
            const factor = _this.compareToRight ? -1 : 1;
            const width = `${md.firstWidth + factor * delta.x}px`;
            getViewPage360().setPanoWidth(width);
          }
        }
        function onDragEnd() {
          document.removeEventListener('mousemove', onDragMove);
          document.removeEventListener('mouseup', onDragEnd);
          document.removeEventListener('touchmove', onDragMove);
          document.removeEventListener('touchend', onDragEnd);
        }
      },
      onDragStart(e) {
        const isTouch = e.type === 'touchstart';
        this.md = {
          e,
          offsetLeft: this.$refs.compareSeparator.offsetLeft,
          firstWidth: getViewPage360().$el.parentElement.offsetWidth,
        };
        const moveEvent = isTouch ? 'touchmove' : 'mousemove';
        const endEvent = isTouch ? 'touchend' : 'mouseup';
        document.addEventListener(moveEvent, this.onDragMove);
        document.addEventListener(endEvent, this.onDragEnd);
      },
      onDragMove(e) {
        const isTouch = e.type === 'touchmove';
        const clientX = isTouch ? e.changedTouches[0].clientX : e.clientX;
        const clientY = isTouch ? e.changedTouches[0].clientY : e.clientY;
        const mdClientX = isTouch ? this.md.e.changedTouches[0].clientX : this.md.e.clientX;
        const mdClientY = isTouch ? this.md.e.changedTouches[0].clientY : this.md.e.clientY;
        const delta = {
          x: clientX - mdClientX,
          y: clientY - mdClientY,
        };
        const factor = this.compareToRight ? -1 : 1;
        const width = `${this.md.firstWidth + factor * delta.x}px`;
        getViewPage360().setPanoWidth(width);
      },
      onDragEnd() {
        document.removeEventListener('mousemove', this.onDragMove);
        document.removeEventListener('mouseup', this.onDragEnd);
        document.removeEventListener('touchmove', this.onDragMove);
        document.removeEventListener('touchend', this.onDragEnd);
      },
      dragElement_CompareTool2(element, direction) {
        var _this = this;
        var md; // remember mouse down info
        element.onmousedown = onMouseDown;
        element.ontouchstart = (e) => {
          md = {
            e,
            offsetLeft: element.offsetLeft,
            firstWidth: getViewPage360().$el.parentElement.offsetWidth,
          };
          element.ontouchmove = onTouchMove;
        };
        function onTouchMove(e) {
          var delta = {
            x: e.changedTouches[0].clientX - md.e.changedTouches[0].clientX,
            y: e.changedTouches[0].clientY - md.e.changedTouches[0].clientY,
          };
          if (direction === 'H') {
            // Horizontal
            let factor = _this.compareToRight ? -1 : 1;
            var width = md.firstWidth + factor * delta.x + 'px';
            getViewPage360().setPanoWidth(width);
          }
        }
        element.ontouchend = (e) => {
          debugLog('ontouchend', e);
          element.ontouchmove = null;
        };
        function onMouseDown(e) {
          md = {
            e,
            offsetLeft: element.offsetLeft,
            firstWidth: getViewPage360().$el.parentElement.offsetWidth,
          };
          document.onmousemove = onMouseMove;
          document.onmouseup = () => {
            document.onmousemove = document.onmouseup = null;
          };
        }
        function onMouseMove(e) {
          debugLog('onMouseMove', e);
          var delta = {
            x: e.clientX - md.e.clientX,
            y: e.clientY - md.e.clientY,
          };
          if (direction === 'H') {
            // Horizontal
            let factor = _this.compareToRight ? -1 : 1;
            var width = md.firstWidth + factor * delta.x + 'px';
            getViewPage360().setPanoWidth(width);
          }
        }
      },
      getAssetAsync() {
        return new Promise((resolve, reject) => {
          fetchAssetModel(this.assetId).then((asset) => {
            let p1 = fetchAssetModels(this.assetId, asset.selectedModel).then((models) => {
              this.$store.state.uploadedModels = models;
            });

            let p2 = levelsService.fetchAssetLevelsParsed(this.assetId).then((levels) => {
              this.$store.state.selectedLevel = levels.find((o) => o.name == asset.selectedLevel);
              this.$store.state.levels = levels;
            });

            Promise.all([p1, p2]).then(() => {
              debugLog('Asset data fetched');
              handleAssetInfo(asset);
              this.$store.commit('setShowLoading', false);
              resolve();
            });
          });
        });
      },
      fetchAndApplyProject(assetId, callback) {
        this.$store.dispatch('REQUEST_DOCUMENT_TYPES');
        const userId = this.$store.getters['ACCOUNT/GET_USER']?.id;
        // fetchAssetModel(assetId).then((asset) => {
        //   fetchAssetModels(assetId, asset.selectedModel).then((models) => {
        //     this.$store.state.uploadedModels = models;
        //   });
        //   fetchAssetLevels(assetId).then((levels) => {
        //     this.$store.state.selectedLevel = levels.find((o) => o.name == asset.selectedLevel);
        //     this.$store.state.levels = levels;
        //   });
        //   handleAssetInfo(asset);
        //   this.$store.commit('setShowLoading', false);
        //   if (callback) callback();
        // });
        const normalEquipmentRequest = fetchAssetNormalEquipment(assetId).then(
          (normalEquipment) => {
            this.$store.commit('setTags', normalEquipment);
            this.$store.commit('SET_AUTOMATIC_TAGS', []);
          },
        );
        const manualPipelinesRequest = fetchAssetManualPipelines(assetId).then(
          (manualPipelines) => {
            this.$store.commit('SET_MANUAL_PIPELINES_TAGS', manualPipelines);
          },
        );
        const manualStructuralRequest = fetchAssetManualStructural(assetId).then(
          (manualStructural) => {
            this.$store.commit('SET_MANUAL_STRUCTURAL_TAGS', manualStructural);
          },
        );
        const annotationsRequest = fetchAssetAnnotations(assetId, userId).then((annotations) => {
          this.$store.commit('setAnnotations', annotations);
        });
        const scaffoldsRequest = fetchAssetScaffold(assetId).then((scaffolds) => {
          this.$store.commit('setScaffolds', scaffolds);
        });
        const inspectionsRequest = fetchAssetInspections(assetId).then((inspections) => {
          this.$store.commit('setInspections', inspections);
          if (inspections?.length) this.$store.dispatch('UPDATE_INSPECTIONS_UNITS');
        });
        Promise.all([
          normalEquipmentRequest,
          manualPipelinesRequest,
          manualStructuralRequest,
          annotationsRequest,
          scaffoldsRequest,
          inspectionsRequest,
        ]).then((responses) => {
          this.$store.state.dataFetched = true;
          if (this.forgeService.getMainExtension() && this.viewer && this.viewer.model) {
            this.forgeService.getMainExtension()?.syncTagsToViewer();
          }
          this.forgeService.update3dIconsInAllViews();
          this.forgeService.dbIdPerObj = [];
        });
        fetchAssetBookmarks(assetId).then((cameras) => {
          this.$store.state.cameras = cameras;
        });
        fetchAssetPointCloudModels(assetId).then((pointClouds) => {
          this.$store.state.pointClouds = pointClouds;
        });
        fetchAssetPointCloudDownloads(assetId).then((pointCloudDownloads) => {
          this.$store.state.pointCloudDownloads = pointCloudDownloads;
        });
        fetchAssetCategories(assetId).then((categories) => {
          //to keep the reference to apply on the UI correctly
          this.$store.state.categories = [...categories];
        });
        projectsService.getByAsset(assetId).then((data) => {
          let show360 = this.$store.state.userSettings.displaySettings.show360;
          let projects = ForgeProject.parseProjectsFromDB(data.data);
          this.$store.state.projects = projects;
          projects.forEach((p) => {
            p.setAllImagesVisibleInViewers(show360);
          });
        });
      },
      dragElement(element, direction) {
        var md; // remember mouse down info
        element.onmousedown = onMouseDown;
        var _this = this;
        function onMouseDown(e) {
          var rightOne = element.nextElementSibling;
          md = {
            e,
            offsetLeft: element.offsetLeft,
            offsetTop: element.offsetTop,
            firstWidth: element.previousElementSibling.offsetWidth,
            secondWidth: rightOne.offsetWidth,
            firstHeight: element.previousElementSibling.offsetHeight,
            secondHeight: rightOne.offsetHeight,
          };
          document.onmousemove = onMouseMove;
          document.onmouseup = () => {
            document.onmousemove = document.onmouseup = null;
          };
        }
        function onMouseMove(e) {
          var delta = {
            x: e.clientX - md.e.clientX,
            y: e.clientY - md.e.clientY,
          };
          if (direction === 'H') {
            // Horizontal
            delta.x = Math.min(Math.max(delta.x, -md.firstWidth), md.secondWidth);
            var rightOne = element.nextElementSibling;
            element.style.left = md.offsetLeft + delta.x + 'px';
            element.previousElementSibling.style.width = md.firstWidth + delta.x + 'px';
            rightOne.style.width = md.secondWidth - delta.x + 'px';
          } else if (direction == 'V') {
            delta.y = Math.min(Math.max(delta.y, -md.firstHeight), md.secondHeight);
            rightOne = element.nextElementSibling;
            element.style.top = md.offsetTop + delta.y + 'px';
            element.previousElementSibling.style.height = md.firstHeight + delta.y + 'px';
            rightOne.style.height = md.secondHeight - delta.y + 'px';
          }
          setTimeout(() => {
            _this.resizeViewers();
          }, 100);
        }
      },
      showNotification(text) {
        this.notificationText = text;
        var _this = this;
        setTimeout(() => {
          _this.notificationText = undefined;
        }, 2000);
      },
      shareDashboard() {
        getStateId((id) => {
          let url = `${this.$store.state.parentSiteUrl}?assetId=${this.$store.state.assetId}&stateId=${id}`;
          showCopiedTextPopup(url, 'Note: URL will expire in 7 days.');
        });
      },
      selectViewOption(index) {
        if (index == 0) {
          if (this.showenViewersLength != 1) {
            if (this.isView1Visible) {
              this.isView2Visible = false;
              this.isView3Visible = false;
              this.isView4Visible = false;
            } else if (this.isView2Visible) {
              this.isView3Visible = false;
              this.isView4Visible = false;
            } else {
              this.isView4Visible = false;
            }
          }
          for (let i = 1; i <= 4; i++) {
            if (!this[`view${i}Type`] && this[`isView${i}Visible`]) {
              this[`isView${i}Visible`] = false;
            }
          }
        } else if (index == 1) {
          if (this.showenViewersLength != 2) {
            if (this.showenViewersLength == 1) {
              let visable = 0;
              for (let i = 1; i <= 4; i++) {
                if (this[`isView${i}Visible`]) {
                  visable = i;
                  break;
                }
              }
              if (visable > 2) {
                this.isView1Visible = true;
              } else {
                this.isView3Visible = true;
              }
            } else if (this.showenViewersLength == 3) {
              let temp = 0;
              for (let i = 1; i <= 4; i++) {
                if (this[`isView${i}Visible`]) {
                  temp += i;
                }
              }
              if (temp % 2 == 0) {
                this.isView2Visible = false;
                this.isView4Visible = false;
              } else {
                this.isView1Visible = false;
                this.isView3Visible = false;
              }
            } else {
              this.isView1Visible = true;
              this.isView2Visible = false;
              this.isView3Visible = true;
              this.isView4Visible = false;
            }
          }
        } else if (index == 2) {
          if (this.showenViewersLength != 3) {
            if (this.showenViewersLength == 1) {
              let visable = 0;
              for (var i = 1; i <= 4; i++) {
                if (this[`isView${i}Visible`]) {
                  visable = i;
                  break;
                }
              }
              if (visable > 2) {
                this.isView1Visible = true;
                this.isView2Visible = true;
              } else {
                this.isView3Visible = true;
                this.isView4Visible = true;
              }
            } else if (this.showenViewersLength == 2) {
              for (let i = 4; i >= 1; i--) {
                if (!this[`isView${i}Visible`]) {
                  if (i == 2 && !this.isView1Visible) {
                    this.isView1Visible = true;
                  } else {
                    this[`isView${i}Visible`] = true;
                  }
                  break;
                }
              }
            } else {
              this.isView2Visible = false;
            }
          }
        } else if (index == 3) {
          this.isView1Visible = true;
          this.isView2Visible = true;
          this.isView3Visible = true;
          this.isView4Visible = true;
        }
        this.showViewsOptions = false;
        if (this.localShowComparasionTool && getViewPage360())
          getViewPage360().toggleComparasionTool();
        setTimeout(() => {
          this.resizeViewers();
        }, 50);
      },
      closeViewer(viewType, viewerIndex) {
        this[`isView${viewerIndex}Visible`] = false;
        this.changeView('', viewerIndex);
      },
      openComparisonsViewers() {
        let indexCount = 0;
        this.beforeCompareState = [];
        for (let i = 1; i <= 4; i++) {
          this.beforeCompareState.push({
            visibility: this[`isView${i}Visible`],
            type: this[`view${i}Type`],
          });
          if (this[`view${i}Type`] == '360' || this[`view${i}Type`] == '3d') {
            indexCount += i;
          }
        }
        if (indexCount == 3 || indexCount == 7) {
          // move 360 viewer in the empty viewer or 2d viewer
          let targetIndex = this.getViewTypeIndex('');
          if (!targetIndex) {
            targetIndex = this.getViewTypeIndex('2d');
          }
          this.changeView('360', targetIndex);
        }
        for (let i = 1; i <= 4; i++) {
          if (this[`view${i}Type`] == '360' || this[`view${i}Type`] == '3d') {
            this[`isView${i}Visible`] = true;
          } else {
            this[`isView${i}Visible`] = false;
          }
        }

        let viewer360Index = this.getViewTypeIndex('360');
        let viewer3dIndex = this.getViewTypeIndex('3d');

        //#region Compare to right
        //Need to check it later
        this.compareToRight =
          (viewer360Index > viewer3dIndex && !this.isViewersParallelY) ||
          (viewer360Index < viewer3dIndex && this.isViewersParallelY);

        this.compareToRight = true;
        //#endregion

        this.$store.state.mainApp.resizeViewers();
      },
      restoreBeforeCompare() {
        for (let i = 1; i <= 4; i++) {
          this[`isView${i}Visible`] = this.beforeCompareState[i - 1].visibility;
          if (this[`view${i}Type`] != this.beforeCompareState[i - 1].type) {
            this.changeView(this.beforeCompareState[i - 1].type, i);
          }
        }
      },
      showHiddenPanel(viewer) {
        this[`isView${this.viewer.index}Visible`] = true;
        this.showViewsOptions = false;
        setTimeout(() => {
          this.resizeViewers();
        }, 50);
      },
      getViewTypeIndex(type) {
        for (let i = 1; i <= 4; i++) {
          if (this[`view${i}Type`] == type || (!type && !this[`view${i}Type`])) {
            return i;
          }
        }
        return 0;
      },
      closeLeftTab() {
        if (this.isDisplaySettingsVisible) {
          this.isDisplaySettingsVisible = false;
          return;
        }

        if (this.isModelBrowserVisible) {
          this.isModelBrowserVisible = false;
          return;
        }

        if (this.selectedTab == 4) {
          this.$store.dispatch('TAGS_TYPES/ONE_STEP_BACK');
        } else if (this.selectedTab == 11) {
          this.$store.dispatch('InspectionsOneStepBack');
        } else {
          this.$store.dispatch('closeLeftTab');
        }
      },
      // closeModelBrowserPanel removed - Model Browser now embedded in Models panel
      toggleLayouts() {
        if (this.localShowComparasionTool) {
          this.$dialog.notify.warning(`Please stop the compare tool first.`, {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }
        this.showViewsOptions = !this.showViewsOptions;

        this.showActivityViewsOptions = false;
      },
      onLogoClick() {
        this.closeLeftTab();
      },
      cancelCmlPicking() {
        this.$store.commit('setIsPickingCmlPoint', false);
        this.$store.commit('setPickingCmlCallback', null);
        this.resetCmlPickingCursor();
        // Close auto-loaded PC
        const pcName = this.$store.state.cmlAutoLoadedPcName;
        if (pcName) {
          const forgeService = this.$store.state.forgeService;
          const pc = this.$store.state.pointClouds.find((p) => p.name === pcName);
          if (forgeService && pc) forgeService.toggleShowingPointCloudIn3D(pc);
          this.$store.commit('setCmlAutoLoadedPcName', null);
        }
      },
      resetCmlPickingCursor() {
        const forgeService = this.$store.state.forgeService;
        if (!forgeService) return;
        const viewer = this.$store.state.viewer;
        if (viewer) forgeService.toggleCrossHairCursorToViewer(false, viewer);
        const planViewer = this.$store.state.planViewer;
        if (planViewer) forgeService.toggleCrossHairCursorToViewer(false, planViewer);
        if (forgeService.panoramaViewer) {
          $('.pnlm-ui').css('cursor', '');
        }
      },
      /** Cancel tag position assignment (follows cancelCmlPicking pattern). */
      cancelPositionAssignment() {
        this.$store.commit('setIsSettingTagPosition', false);
        this.$store.commit('setPositionAssignmentTag', null);
        this.$store.commit('setPositionAssignmentCallback', null);
        this.resetCmlPickingCursor(); // Reuse same cursor reset — clears crosshair on all viewers
        // Close auto-loaded PC (same cleanup as CML)
        const pcName = this.$store.state.positionAutoLoadedPcName;
        if (pcName) {
          const forgeService = this.$store.state.forgeService;
          const pc = this.$store.state.pointClouds.find((p) => p.name === pcName);
          if (forgeService && pc) forgeService.toggleShowingPointCloudIn3D(pc);
          this.$store.commit('setPositionAutoLoadedPcName', null);
        }
      },
    },
    computed: {
      facility: {
        get() {
          return this.$store.getters['FACILITY/GET_FACILITY'];
        },
        set(value) {
          this.$store.commit('FACILITY/SET_FACILITY', value);
        },
      },
      assetId: {
        get() {
          return this.$router.currentRoute.params.id;
        },
      },
      view1Type: {
        get() {
          return this.$store.getters['GET_VIEW_1_TYPE'];
        },
        set(value) {
          this.$store.commit('SET_VIEW_1_TYPE', value);
        },
      },
      view2Type: {
        get() {
          return this.$store.getters['GET_VIEW_2_TYPE'];
        },
        set(value) {
          this.$store.commit('SET_VIEW_2_TYPE', value);
        },
      },
      view3Type: {
        get() {
          return this.$store.getters['GET_VIEW_3_TYPE'];
        },
        set(value) {
          this.$store.commit('SET_VIEW_3_TYPE', value);
        },
      },
      view4Type: {
        get() {
          return this.$store.getters['GET_VIEW_4_TYPE'];
        },
        set(value) {
          this.$store.commit('SET_VIEW_4_TYPE', value);
        },
      },
      isView1Visible: {
        get() {
          return this.$store.getters['GET_IS_VIEW_1_VISIBLE'];
        },
        set(value) {
          this.$store.commit('SET_IS_VIEW_1_VISIBLE', value);
        },
      },
      isView2Visible: {
        get() {
          return this.$store.getters['GET_IS_VIEW_2_VISIBLE'];
        },
        set(value) {
          this.$store.commit('SET_IS_VIEW_2_VISIBLE', value);
        },
      },
      isView3Visible: {
        get() {
          return this.$store.getters['GET_IS_VIEW_3_VISIBLE'];
        },
        set(value) {
          this.$store.commit('SET_IS_VIEW_3_VISIBLE', value);
        },
      },
      isView4Visible: {
        get() {
          return this.$store.getters['GET_IS_VIEW_4_VISIBLE'];
        },
        set(value) {
          this.$store.commit('SET_IS_VIEW_4_VISIBLE', value);
        },
      },
      dataFetched() {
        return this.$store.state.dataFetched;
      },
      editTag: {
        get() {
          return this.$store.getters['GET_EDITING_TAG'];
        },
      },
      editInspectionTag: {
        get() {
          return this.$store.getters['GET_EDITING_AMIS_TAG'];
        },
      },
      selectedTabName: function () {
        // Special cases: show tag name when editing
        if (this.selectedTab === PANEL_TAB.TAGS && this.editTag?.name) {
          return this.editTag.name;
        }
        if (this.selectedTab === PANEL_TAB.INSPECTION && this.editInspectionTag?.name) {
          return this.editInspectionTag.name;
        }
        // Use TAB_NAMES lookup for all other cases
        return TAB_NAMES[this.selectedTab] || '';
      },
      isTabShown: function () {
        return this.$store.state.isTabShown;
      },
      totalNavWidth() {
        return this.$store.getters['GENERAL/GET_TOTAL_NAV_WIDTH'];
      },
      viewCompareWidth: function () {
        if (this.isViewersParallelY) {
          return '100%';
        }
        if (!this.localShowComparasionTool) return 'auto';
        return `calc(100vw - ${this.totalNavWidth}px)`;
      },
      allViewersHeight: function () {
        return '100%';
      },
      showRealityDashboard() {
        return this.$store.getters['getIsRealityDashoardShown'];
      },
      localSuccessMessage: function () {
        return this.$store.getters['getSuccessMessage'] ?? '';
      },
      localShowAddProperty: function () {
        return this.$store.getters['getShowAddProperty'] ?? false;
      },
      showAMISTab: function () {
        return !!this.$store.state.forgeSettings?.isInspectionsOn;
      },
      showCloseBtn: function () {
        let opened = 0;
        if (this.isView1Visible) opened++;
        if (this.isView2Visible) opened++;
        if (this.isView3Visible) opened++;
        if (this.isView4Visible) opened++;
        return opened > 1 && !this.localShowComparasionTool;
      },
      localShowComparasionTool: function () {
        return this.$store.getters['getShowComparasionTool'];
      },
      hiddenViewers: function () {
        let hidden = [];
        for (var i = 1; i <= 4; i++) {
          if (!!this[`view${i}Type`] && !this[`isView${i}Visible`]) {
            hidden.push({
              index: i,
              type: this[`view${i}Type`],
            });
          }
        }
        return hidden;
      },
      showenViewersLength: function () {
        let length = 0;
        for (var i = 1; i <= 4; i++) {
          if (this[`isView${i}Visible`]) {
            length++;
          }
        }
        return length;
      },
      isViewersParallelY: function () {
        return (
          this.showenViewersLength == 2 &&
          ((this.isView1Visible && this.isView2Visible) ||
            (this.isView3Visible && this.isView4Visible))
        );
      },
      localShowLoading() {
        return this.$store.getters['getShowLoading'];
      },
      pending360Zoom() {
        return this.$store.getters['getPending360Zoom'];
      },
      isCorrespondingPcLoading: {
        get() {
          return this.$store.getters.getIsCorrespondingPcLoading;
        },
        set(value) {
          this.$store.commit('setIsCorrespondingPcLoading', value);
        },
      },
      selectedTab() {
        return this.$store.getters['getSelectedTab'];
      },

      showCropLevelPopup: {
        get() {
          return this.$store.getters['getShowCropLevelPopup'];
        },
        set(value) {
          this.$store.commit('setShowCropLevelPopup', value);
        },
      },
      showPointCloudDisplayPopup: {
        get() {
          return this.$store.state.showPointCloudDisplayPopup;
        },
      },
      showLeftButtons: function () {
        return this.$store.getters['getShowLeftButtons'];
      },

      showUpdateDocumentPopup: function () {
        return this.$store.getters['GET_SHOW_UPDATE_DOCUMENT_POPUP'];
      },
      showSelectDashboardPopup: function () {
        return this.$store.getters['GET_SHOW_SELECT_DASHBOARD_POPUP'];
      },
      showTagDynamicGraph: function () {
        return !!this.$store.getters['GET_TAG_DYNAMIC_GRAPH'];
      },
      isEmptyModel: function () {
        return this.$store.getters['IS_EMPTY_MODEL'];
      },
      showRealityVerticalSpace() {
        return this.showRealityDashboard;
      },
      compareModeReady: function () {
        let result = 0;
        for (let i = 1; i <= 4; i++) {
          if (this[`view${i}Type`] == '360' || this[`view${i}Type`] == '3d') {
            result++;
          }
        }
        return result == 2;
      },
      forgeService: {
        get() {
          return this.$store.state.forgeService;
        },
      },
      viewer: {
        get() {
          return this.$store.state.viewer;
        },
      },
      isAMIS() {
        return (
          this.$store.state.forgeSettings?.isAMIS &&
          this.$store.getters['GET_ALL_AMIS_TAGS_IDS']?.length
        );
      },
      isDashboardFullScreen: {
        get() {
          return this.$store.getters['GET_IS_DASHBOARD_FULL_SCREEN'];
        },
        set(value) {
          this.$store.commit('SET_IS_DASHBOARD_FULL_SCREEN', value);
        },
      },
      showInspectionDashboard: {
        get() {
          return this.$route?.query?.selectedPageTitle === 'New Inspection Dashboard';
        },
      },
      showSisTestingDashboard: {
        get() {
          return this.$route?.query?.selectedPageTitle === 'Activity Tracker';
        },
      },
      showWorkPackProgressDashboard: {
        get() {
          return this.$route?.query?.selectedPageTitle === 'Work Pack Tag Progress';
        },
      },
      filterDockInset() {
        // Mirror view.vue: the value that actually changes the viewer's box width.
        const base = this.$store.getters['GENERAL/GET_INSPECTION_FILTER_DOCK_INSET'];
        return isRouteDashboardActive(this.$route) ? 0 : base;
      },
      isViewerSideMenuMinimized: {
        get() {
          return this.$store.getters['GENERAL/GET_VIEWER_SIDE_MENU_MINIMIZED'];
        },
        set(value) {
          this.$store.commit('GENERAL/SET_VIEWER_SIDE_MENU_MINIMIZED', value);
        },
      },
      isLeftMenuPinned: {
        get() {
          return this.$store.getters['GET_IS_LEFT_MENU_PINNED'];
        },
        set(value) {
          this.$store.commit('SET_IS_LEFT_MENU_PINNED', value);
        },
      },
      showNavMenu: {
        get() {
          return this.$store.getters['GET_SHOW_NAV_MENU'];
        },
        set(value) {
          this.$store.commit('SET_SHOW_NAV_MENU', value);
        },
      },
      isDashboardOpened: {
        get() {
          return this.$store.getters['GENERAL/GET_IS_DASHBOARD_OPENED'];
        },
        set(value) {
          this.$store.commit('GENERAL/SET_IS_DASHBOARD_OPENED', value);
        },
      },
      isviewrMinimized: {
        get() {
          return this.$store.getters['GENERAL/GET_VIEWER_SIDE_MENU_MINIMIZED'];
        },
        set(value) {
          this.$store.commit('GENERAL/SET_VIEWER_SIDE_MENU_MINIMIZED', value);
        },
      },
      isDisplaySettingsVisible: {
        get() {
          return this.$store.getters['GET_IS_DISPLAY_SETTINGS_VISIBLE'];
        },
        set(val) {
          this.$store.commit('SET_IS_DISPLAY_SETTINGS_VISIBLE', val);
        },
      },
      // isModelBrowserVisible computed property removed - Model Browser now embedded in Models panel
      areViewsLocked() {
        return this.$store.state.areViewsLocked;
      },
      isPickingCmlPoint() {
        return this.$store.state.isPickingCmlPoint;
      },
      isSettingTagPosition() {
        return this.$store.state.isSettingTagPosition;
      },
      positionAssignmentTagName() {
        return this.$store.state.positionAssignmentTag?.name || '';
      },
      positionAssignmentHint() {
        // Every tag kind can be placed from either view, so there is one message.
        return 'Click on the 3D model or 360 scan to set the position.';
      },
    },
  };
</script>

<style lang="scss">
  :root {
    /* root element */
    --primary-color: #ad5087;
    --light-primary-color: #e1c3d2;
    --light-primary-color2: #f9f1f6;
    --dark-primary-color: #78325c;

    /* Secondry colors */
    /* --secondry-color: #8ccecd;
    --light-secondry-color: #92bacc;
    --dark-secondry-color: #68bab9; */

    --secondry-color: #51a0c4;
    --light-secondry-color: #92bacc;
    --dark-secondry-color: #377896;

    --x-axis-color: #e62c2c;
    --y-axis-color: #0bb80b;
    --z-axis-color: #2c2cf1;

    --main-font: 'Popins', sans-serif;
  }

  .viewers-2-compare-mode {
    position: absolute;
    width: 100% !important;
    height: 100%;
    top: 0;
    overflow: hidden;
  }

  .viewers-2-compare-mode.compare-right {
    right: 0;
  }

  .viewers-2-compare-mode:not(.compare-right) {
    left: 0;
  }

  .view-compare-mode {
    position: absolute !important;
    height: 100% !important;
    top: 0 !important;
  }

  .view-compare-mode.compare-right {
    right: 0;
  }

  .view-compare-mode:not(.compare-right) {
    left: 0;
  }

  .all-4-viewers-container {
    display: flex;
    flex-grow: 1;
    height: 100%;
    overflow: hidden;
    position: relative;
  }

  .all-2-viewers-container {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    width: calc(50% - 2px);
  }

  .left-info-container {
    height: 100%;
    display: flex;
    animation-name: left-menu-animation;
    animation-duration: 0.5s;
  }

  @keyframes left-menu-animation {
    from {
      width: 0;
    }

    to {
      width: auto;
    }
  }

  .views-options {
    color: white;
    background-color: var(--v-primary-darken2);
    padding: 4px;
    height: 90px;
    position: absolute;
    width: 330px;
    left: calc(50% - 170px);
    top: calc(50% - 47px);
    z-index: 10;
    /* flex-flow: row-reverse; */
    border-radius: 5px;
    font-size: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    row-gap: 15px;
  }

  .activity-views-options {
    width: 250px;
    color: white;
    background-color: var(--v-primary-darken2);
    padding: 4px;
    height: 90px;
    position: absolute;
    left: 50px;
    top: 340px;
    z-index: 10;
    border-radius: 5px;
    font-size: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    row-gap: 15px;
  }

  .view-options-cards {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: row-reverse;
  }

  .viewer-options-names {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    flex-direction: row;
  }

  .view-option-name {
    width: 40px;
    height: 40px;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    color: var(--v-primary-darken2);
    border-radius: 5px;
    font-style: italic;
  }

  .view-option-name:hover {
    background: var(--v-primary-lighten3);
    color: white;
    cursor: pointer;
  }

  .view-option-icon {
    text-align: center;
    width: 50px;
    height: 50px;
    cursor: pointer;
    margin: 3px;
    color: var(--v-primary-base);
  }

  .view-option-icon:hover {
    color: var(--v-primary-lighten3);
  }

  /* grids */
  .single-grid {
    background-size: cover;
    background-image: url(../images/grid1.png);
  }

  .single-grid:hover {
    background-image: url(../images/grid1-hover.png);
  }

  .double-grid {
    background-size: cover;
    background-image: url(../images/grid2.png);
  }

  .double-grid:hover {
    background-image: url(../images/grid2-hover.png);
  }

  .triple-grid {
    background-size: cover;
    background-image: url(../images/grid3.png);
  }

  .triple-grid:hover {
    background-image: url(../images/grid3-hover.png);
  }

  .horizontal-grid {
    height: 60px;
    background-position: center;
    background-size: cover;
    background-image: url(../images/grid5.png);
  }

  .horizontal-grid:hover {
    background-image: url(../images/grid5-hover.png);
  }

  .quad-grid,
  .quad-grid-gray {
    background-size: cover;
    background-image: url(../images/grid4.png);
  }

  .quad-grid:hover {
    background-image: url(../images/grid4-hover.png);
  }

  .quad-grid-gray:hover {
    background-image: url(../images/grid4-hovergray.png);
  }

  .corner-close-btn {
    font-weight: 500;
    font-size: 12px;
    padding-top: 2px;
    font-family: 'Popins', sans-serif !important;
    background-color: #adadad;
    color: white;
    border-radius: 18pc;
    width: 22px;
    height: 20px;
    text-align: center;
    margin: 0px 15px;
    position: relative;
    cursor: pointer;
  }

  .corner-close-btn:hover {
    background-color: var(--v-primary-darken2);
  }

  .top-title-bar {
    width: 100%;
    display: flex;
    height: 35px;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    margin-bottom: 10px;
  }

  .top-title-name {
    /* flex-grow: 1; */
    padding: 3px;
    text-align: center;
    font: bold 17px Helvetica, Arial, Sans-Serif;
  }

  .slash-btn {
    width: 18px;
    height: 23px;
    position: absolute;
    right: 3px;
    top: 3%;
    color: gray;
    background-color: black;
    border-radius: 0 18px 16px 0;
    cursor: pointer;
    padding: 5px 2px;
  }

  .left-panel {
    width: 300px;
    height: calc(100% - 36px);
    position: fixed;
    left: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    z-index: 999;
  }

  .canvas-dimension {
    position: absolute;
    z-index: 22;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    pointer-events: none;
  }

  .notification-div {
    position: absolute;
    top: 10px;
    width: auto;
    height: 20px;
    background-color: lightgreen;
    font-family: var(--main-font);
    border: 1px solid green;
    text-align: center;
    padding: 5px;
    font-size: 16px;
    font-weight: bold;
    border-radius: 6px;
    animation-name: notification-animation;
    animation-duration: 0.5s;
    z-index: 9;
  }

  @keyframes notification-animation {
    from {
      height: 0;
      font-size: 0;
    }

    to {
      height: 20px;
      font-size: 16px;
    }
  }

  .viewers-container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: row;
    position: absolute;
    justify-content: center;
  }

  #separator {
    /* cursor: col-resize; */
    background-color: lightgray;
    width: 3px;
    height: 100%;

    /* Prevent the browser's built-in drag from interfering */
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  #compare-separator {
    cursor: col-resize;
    background-color: lightgray;
    width: 5px;
    height: 100%;
    pointer-events: all;
    position: absolute;
    z-index: 7;

    /* Prevent the browser's built-in drag from interfering */
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  .compare-separator-circle {
    cursor: col-resize;
    background-color: lightgray;
    width: 18px;
    height: 64px;
    pointer-events: all;
    top: 50%;
    margin-left: 2px;
    z-index: 7;
    user-select: none;
    border-bottom-right-radius: 22px;
    border-top-right-radius: 22px;
    border-top: 1px solid gray;
    border-right: 1px solid gray;
    border-bottom: 1px solid gray;
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;

    /* Prevent the browser's built-in drag from interfering */
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  .compare-separator-circle:hover {
    background-color: var(--v-primary-darken2);
  }

  #separator2,
  #separator3 {
    /* cursor: row-resize; */
    background-color: lightgray;
    width: 100%;
    height: 3px;

    /* Prevent the browser's built-in drag from interfering */
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  .right-btn {
    right: 5px;
  }

  .bottom-right {
    position: absolute;
    right: 65px;
    bottom: 5px;
    color: white;
    font-size: 16px;
    font-weight: inherit;
  }

  .left-label {
    left: 41px;
    border-radius: 0 4px 4px 0;
  }

  .rotate-all-2-viewers-container {
    display: flex;
    flex-direction: row !important;
    flex-grow: 1;
    width: 100% !important;
  }

  .rotate-all-2-viewers-container #separator2,
  .rotate-all-2-viewers-container #separator3 {
    display: none;
  }

  .rotate-all-2-viewers-container .page-view {
    height: 100% !important;
  }

  .dashboard-space {
    width: calc(50vw - 20px);
    height: 100%;
    flex-shrink: 0;
    background-color: transparent;
    pointer-events: none;
  }

  .dashboard-space.full-screen-space {
    width: calc(100% - 40px);
  }

  .alert-box.error {
    display: none !important;
  }

  #mainViewApp input[type='text'],
  #mainViewApp input[type='number'] {
    height: 35px;
  }

  .top-title-bar .panel-header {
    flex: 1;
    text-align: center;
    white-space: nowrap;
  }

  #tags-panel {
    background-color: var(--v-bg_Greeny_Gray-base);
    height: 100%;
  }

  .tag-info {
    height: calc(100%);
    display: flex;
    flex-direction: column;
    overflow: auto;
    background-color: rgba(34, 34, 34, 0.7);
    color: var(--v-black-base);
    padding: 0;
    position: relative;
    overflow: hidden;

    .scrollable-content {
      height: 100%;
      min-height: 100px;
      padding-left: 5px;
      padding-right: 0;
    }

    .tag-info-tabs {
      // width: max-content + flex: 0 !important were preventing Vuetify's
      // overflow detection from firing — the container was sized to its
      // content, so containerWidth === contentWidth and v-slide-group never
      // saw overflow. Removed so the tab strip is bounded by the panel and
      // Vuetify can render the v-slide-group__prev / __next chevrons when
      // tabs overflow (paired with :mobile-breakpoint="0" on the v-tabs to
      // remove the mobile-suppression gate). See Wave C-δ T036, 2026-05-19.
      min-width: 0;

      // Hide the v-slide-group prev/next chevrons when they're disabled
      // (no scroll possible in that direction). Without this, Vuetify
      // reserves a ~36px slot for the disabled button which renders as
      // empty whitespace at the start/end of the tab strip. Tradeoff: a
      // small layout shift when the user scrolls past the first tab and
      // the prev chevron transitions disabled→enabled. That's acceptable
      // because the shift only happens during active interaction; idle
      // (the common case) renders flush against the panel edge.
      .v-slide-group__prev.v-slide-group__prev--disabled,
      .v-slide-group__next.v-slide-group__next--disabled {
        display: none;
      }

      .v-slide-group__next,
      .v-slide-group__prev {
        min-width: 28px;
      }
    }

    .v-window {
      &.v-tabs-items {
        background-color: transparent;
      }
      overflow: auto;
    }

    .property-row {
      display: flex;
      flex-direction: column;
      gap: 4px;
      font-size: 16px;
      padding: 8px 0;

      .property-key {
        font-weight: 500;
      }

      .property-value {
        font-weight: 400;
      }
    }
  }

  #documents.v-window-item--active {
    display: flex;
  }

  #local-absolute-loading {
    width: 100%;
    position: absolute;
    height: 100%;
    z-index: 999;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 32px;
    background: #f8f9fa;
  }

  .loading-progress-track {
    width: 200px;
    height: 2px;
    background: #e2e6ea;
    border-radius: 1px;
    overflow: hidden;
  }

  .loading-progress-fill {
    width: 40%;
    height: 100%;
    background: #03624c;
    border-radius: 1px;
    animation: loading-slide 1.6s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  }

  @keyframes loading-slide {
    0% {
      transform: translateX(-100%);
      width: 40%;
    }
    50% {
      width: 60%;
    }
    100% {
      transform: translateX(350%);
      width: 40%;
    }
  }

  .loading-label {
    font-size: 12px;
    color: #8c959f;
    letter-spacing: 0.5px;
    margin-top: -16px;
  }

  /* .model-browser-panel CSS removed - Model Browser now embedded in Models panel */
</style>
